import RezRateTable from 'Body/RezRATE/Common/RezRateTable/RezRateTable';
import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import style from './ApplicationsPage.style';
import { useKindeAuth } from '@kinde-oss/kinde-auth-react';
import { fetchData, fetchDataAgnostic } from 'Services/dataApi';
import { formatBearerToken } from 'Common.functions';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { Button, Modal, ModalBody, ModalHeader } from 'reactstrap';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import ApplicationForm from '../ApplyPage/ApplicationForm/ApplicationForm';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronCircleLeft, faChevronLeft, faMagnifyingGlass } from '@fortawesome/free-solid-svg-icons';
import Swal from 'sweetalert2';

dayjs.extend(utc);
dayjs.extend(timezone);

const ApplicationsPage = (props) => {
  const { applicantDetails, fetchApplicant } = props;

  // State variables
  const [applications, setApplications] = useState([]);

  const [showApplicationDetailModal, setShowApplicationDetailModal] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState(null);

  const [isLoading, setIsLoading] = useState(false);
  const [isGettingApplication, setIsGettingApplication] = useState(false);

  const [isFetchingApplcationDetails, setIsFetchingApplicationDetails] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [applicationDetails, setApplicationDetails] = useState(null);

  const applicationDetailsRef = useRef(null);
  const selectedApplicationRef = useRef(null);

  const kindeAuth = useKindeAuth();
  const { isAuthenticated, login, getToken } = kindeAuth || {};

  const query = new URLSearchParams(useLocation().search);
  const history = useHistory();

  const { pathname } = history.location;
  // Alert functionality replaced with toast

  useEffect(() => {
    const handleResize = () => {
      const windowWidth = window.innerWidth;
      const windowHeight = window.innerHeight;

      if (windowWidth < windowHeight && windowWidth < 1100) {
        setIsMobile(true);
      } else {
        setIsMobile(false);
      }
    };

    window.addEventListener('resize', handleResize);
    handleResize();

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const headers = [
    {
      dataKey: 'organizationName',
      label: 'Organization',
      cellFormatter: (cellData, row) => {
        return <div style={{ fontWeight: 'bold' }}>{row.Organization.OrganizationName || ''}</div>;
      },
      width: 250,
    },
    { dataKey: 'programName', label: 'Program', width: 250 },
    { dataKey: 'submitted', label: 'Submitted', width: 250 },
    {
      dataKey: 'status',
      label: 'Status',
      width: 250,
      cellFormatter: (cellData, row) => {
        const colors = {
          Submitted: 'orange',
          Rejected: 'red',
          Registered: 'green',
          'In Review': 'blue',
        };
        return <div style={{ fontWeight: 'bold', color: colors[row.status] }}>{row.status || ''} </div>;
      },
    },
    {
      dataKey: 'view',
      label: '',
      width: 80,
      cellFormatter: (cellData, row) => {
        return (
          <Button
            size="sm"
            color="primary"
            disabled={isGettingApplication || isLoading}
            onClick={() => {
              getApplication({
                pk_ApplicationRecord: row.pk_ApplicationRecord,
                pk_Department: row.fk_Department,
                pk_Season: row.fk_Season,
                selectedApplication: row,
                cb: (err) => {
                  if (err) {
                    return;
                  }
                  setSelectedApplication(row);
                  setShowApplicationDetailModal(true);
                },
              });
            }}
          >
            <FontAwesomeIcon icon={faMagnifyingGlass} />
          </Button>
        );
      },
    },
  ];

  const mobileHeaders = [
    {
      dataKey: 'organizationName',
      label: 'Application',
      cellFormatter: (cellData, row) => {
        const { programName, submitted, Organization } = row;
        const { OrganizationName } = Organization || {};
        return (
          <div style={{ width: '100%', paddingLeft: 10 }}>
            <div style={{ fontWeight: 'bold' }}>{OrganizationName || ''}</div>
            <div style={{ fontSize: 12 }}>{programName}</div>
            <div style={{ fontSize: 12 }}>{submitted}</div>
          </div>
        );
      },
      width: 'calc(50vw - 24px)',
    },
    {
      dataKey: 'status',
      label: 'Status',
      width: 'calc(50vw - 24px)',
      cellFormatter: (cellData, row) => {
        const { status } = row;
        // if (IsSubmitted) {
        //   status = 'Submitted';
        // } else if (IsRejected) {
        //   status = 'Rejected';
        // } else if (IsRegistered) {
        //   status = 'Registered';
        // } else if (IsUnderReview) {
        //   status = 'In Review';
        // }
        const colors = {
          Submitted: 'orange',
          Rejected: 'red',
          Registered: 'green',
          'In Review': 'blue',
        };
        return <div style={{ fontSize: 15, fontWeight: 'bold', color: colors[status] }}>{status}</div>;
      },
    },
  ];

  // Effects
  useEffect(() => {
    if (isAuthenticated && kindeAuth && applicantDetails && applicantDetails.pk_Applicant) {
      if (!isLoading) {
        getApplications();
      }
    }
  }, [isAuthenticated, applicantDetails]);

  useEffect(() => {
    const pk_DepartmentFromURL = query.get('selectedApplication');

    if (pk_DepartmentFromURL) {
      const toShow = applications.find((app) => {
        return app.fk_Department == pk_DepartmentFromURL;
      });

      if (toShow) {
        setSelectedApplication(toShow);
        getApplication({
          pk_ApplicationRecord: toShow.pk_ApplicationRecord,
          cb: () => {
            setShowApplicationDetailModal(true);
            history.push('/applicant/profile/applications');
          },
        });
      }
    }
  }, [pathname, applications]);

  useEffect(() => {
    selectedApplicationRef.current = selectedApplication;
  }, [selectedApplication]);

  const getApplications = async () => {
    const { pk_Applicant } = applicantDetails || {};
    setApplications([]);
    setIsLoading(true);
    const token = await getToken();
    fetchDataAgnostic('applicant/application/list', { pk_Applicant }, formatBearerToken(token))
      .then((res) => {
        const newData = res.data.map((item) => {
          const {
            Department,
            IsRejected,
            IsRegistered,
            IsUnderReview,
            IsWithdrawn,
            IsSubmitted,
            TimestampOfApplicationSubmission,
          } = item;
          const { DepartmentName } = Department;

          let status = 'Not Submitted';

          if (IsRejected) {
            status = 'Rejected';
          } else if (IsRegistered) {
            status = 'Registered';
          } else if (IsSubmitted) {
            status = 'Submitted';
          } else if (IsUnderReview) {
            status = 'In Review';
          }

          return {
            programName: DepartmentName || '',
            ...item,
            status,
            submitted: dayjs(TimestampOfApplicationSubmission).format('MMMM DD, YYYY'),
          };
        });

        setIsLoading(false);
        setApplications(newData);

        if (selectedApplicationRef.current) {
          const toShow = newData.find((app) => {
            return app.pk_ApplicationRecord == selectedApplicationRef.current.pk_ApplicationRecord;
          });

          if (toShow) {
            setSelectedApplication(toShow);
            getApplication({
              pk_ApplicationRecord: toShow.pk_ApplicationRecord,
              cb: () => {
                // setShowApplicationDetailModal(true);
                // history.push('/applicant/profile/applications');
              },
            });
          }
        }
      })
      .catch((err) => {
        setIsLoading(false);
        console.log('err: ', err);
      });
  };

  const getApplication = async (data) => {
    const { pk_ApplicationRecord, cb, pk_Season, pk_Department, selectedApplication = {} } = data;
    const token = await getToken();
    setIsGettingApplication(true);
    fetchDataAgnostic(
      // 'application/application',
      // 'department/application/applicant',
      'applicant/application',
      { pk_ApplicationRecord: pk_ApplicationRecord },
      formatBearerToken(token),
    )
      .then((res) => {
        if (!res.data) {
          // console.log('throwing err!!');

          throw res;
        }
        const { ApplicantSubmission = {}, ApplicationRecord = {}, Department = {} } = res.data;
        const newApplicationDetails = {
          ...ApplicantSubmission,
          ...ApplicationRecord,
          Department,
          status: selectedApplication.status,
        };

        applicationDetailsRef.current = newApplicationDetails;
        setApplicationDetails(newApplicationDetails);
        setIsGettingApplication(false);
        setTimeout(() => {
          if (cb) {
            cb(null);
          }
        }, 300);
      })
      .catch((err) => {
        toast.error('Error fetching application details');
        console.log('getApplication err: ', err);
        if (cb) {
          cb(err);
        }
        setIsGettingApplication(false);
      });
  };

  const renderApplicationDetailModal = () => {
    const { programName = '', submitted = '', status = '' } = selectedApplication || {};
    const { IsSubmitted } = applicationDetailsRef.current || {};
    return (
      <Modal isOpen={showApplicationDetailModal && !isMobile} centered style={{ maxWidth: '90vw' }}>
        <ModalHeader
          toggle={() => {
            if (hasUnsavedChanges) {
              Swal.fire({
                title: 'Discard Unsaved Changes?',
                text: 'Are you sure you want to discard changes?',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: 'Discard',
                cancelButtonText: 'No',
              }).then((result) => {
                if (result.isConfirmed) {
                  setShowApplicationDetailModal(false);
                  setSelectedApplication(null);
                }
              });
            } else {
              setShowApplicationDetailModal(false);
              setSelectedApplication(null);
            }
          }}
        >
          Application for {programName ? programName : 'Unnamed Program'}
        </ModalHeader>
        <ModalBody>
          <div style={style.simpleRow}>
            <div style={style.simpleColumn}>
              <ApplicationForm
                isEdit={selectedApplication && selectedApplication.status === 'Not Submitted'}
                setExternalHasUnsavedChanges={setHasUnsavedChanges}
                departmentData={applicationDetailsRef.current ? applicationDetailsRef.current.Department : {}}
                applicationDetails={applicationDetailsRef.current}
                selectedOpening={selectedApplication}
                applicantDetails={applicantDetails}
                isLoading={isGettingApplication || isLoading}
                isReadOnly={
                  applicationDetailsRef.current
                    ? applicationDetailsRef.current.IsImported || applicationDetailsRef.current.IsRejected
                    : false
                } // Add other flags for disabled fieldsdisabled fields
                onApply={() => {
                  setShowApplicationDetailModal(false);
                  setSelectedApplication(() => {
                    return null;
                  });
                  fetchApplicant();
                }}
                onSave={() => {
                  getApplications();
                }}
              />
              {/* <div style={style.simpleRow}>Program: {programName}</div>
              <div style={style.simpleRow}>Submitted: {submitted}</div>
              <div style={style.simpleRow}>Status: {status}</div> */}
            </div>
          </div>
        </ModalBody>
      </Modal>
    );
  };

  const { IsSubmitted } = applicationDetailsRef.current || {};
  return (
    <div
      style={{
        ...style.simpleRow,
        marginTop: isMobile ? 10 : 50,
        justifyContent: 'center',
        width: 'calc(100vw - 30px)',
      }}
    >
      <div
        style={{
          ...style.simpleColumn,
          overflow: 'hidden',
          justifyContent: 'flex-start',
          alignItems: 'flex-start',
          height: isMobile ? 'calc(100vh - 180px)' : 'auto',
          width: isMobile ? '100%' : 'auto',
        }}
      >
        {isMobile && showApplicationDetailModal ? (
          <div style={{ ...style.simpleRow, justifyContent: 'flex-start', marginBottom: 5 }}>
            <Button
              size="sm"
              color="primary"
              onClick={() => {
                if (hasUnsavedChanges) {
                  Swal.fire({
                    title: 'Discard Unsaved Changes?',
                    text: 'Are you sure you want to discard changes?',
                    icon: 'warning',
                    showCancelButton: true,
                    confirmButtonText: 'Discard',
                    cancelButtonText: 'No',
                  }).then((result) => {
                    if (result.isConfirmed) {
                      setShowApplicationDetailModal(false);
                      setSelectedApplication(null);
                    }
                  });
                } else {
                  setShowApplicationDetailModal(false);
                  setSelectedApplication(null);
                }
              }}
            >
              <div style={{ ...style.simpleRow, justifyContent: 'space-between' }}>
                <FontAwesomeIcon icon={faChevronCircleLeft} style={{ marginRight: 10 }} />
                Back
              </div>
            </Button>
          </div>
        ) : null}
        {isMobile && showApplicationDetailModal && selectedApplication ? (
          <div style={{ ...style.simpleRow, justifyContent: 'flex-start', width: '100%' }}>
            <ApplicationForm
              isEdit={selectedApplication && selectedApplication.status === 'Not Submitted'}
              setExternalHasUnsavedChanges={setHasUnsavedChanges}
              departmentData={applicationDetailsRef.current ? applicationDetailsRef.current.Department : {}}
              applicationDetails={applicationDetailsRef.current}
              selectedOpening={selectedApplication}
              applicantDetails={applicantDetails}
              isLoading={isGettingApplication || isLoading}
              isReadOnly={
                applicationDetailsRef.current
                  ? applicationDetailsRef.current.IsSubmitted || applicationDetailsRef.current.IsRejected
                  : false
              } // Add other flags for disabled fields
              onApply={() => {
                setShowApplicationDetailModal(false);
                setSelectedApplication(() => {
                  return null;
                });
                fetchApplicant();
              }}
              onSave={() => {
                getApplications();
              }}
            />
          </div>
        ) : (
          <RezRateTable
            columns={isMobile ? mobileHeaders : headers}
            data={applications}
            tableBodyHeight={'calc(100vh - 230px)'}
            onRowClick={(row) => {
              getApplication({
                pk_ApplicationRecord: row.pk_ApplicationRecord,
                pk_Department: row.fk_Department,
                pk_Season: row.fk_Season,
                selectedApplication: row,
                cb: (err) => {
                  if (err) {
                    return;
                  }
                  setSelectedApplication(row);
                  setShowApplicationDetailModal(true);
                },
              });
              // setSelectedApplication(row);
              // setShowApplicationDetailModal(true);
            }}
            rowStyle={(row) => {
              const styleToReturn = {
                cursor: 'pointer',
              };

              if (
                selectedApplication &&
                selectedApplication.fk_Department == row.fk_Department &&
                selectedApplication.fk_ApplicationOpening == row.fk_ApplicationOpening
              ) {
                styleToReturn.backgroundColor = '#d9f5ff';
              }
              return styleToReturn;
            }}
            noDataMessage={isLoading ? <div>Loading...</div> : <div>No Data</div>}
          />
        )}
      </div>
      {renderApplicationDetailModal()}
    </div>
  );
};

export default ApplicationsPage;
